import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { compareDateObjects } from '~/modules/common/dates/compare';
import { mapRepliconDateToMidnightUTCString } from '~/modules/common/dates/convert';
import { roundToDecimals } from '~/modules/resourcing/common/util/scheduleUtil';
import { useGetScheduledHoursForUserAndDateRangeCallback } from '~/modules/resourcing/common/hooks/useGetScheduleHoursForUserAndDateRange';
import { ALLOCATION_PERIOD } from '../../AllocationByPeriod/components/AllocationPeriodTabs';

export const INPUT_FIELDS_MAP = {
  STARTDATE_FIELD: 'startDate',
  ENDDATE_FIELD: 'endDate',
  SUBMIT_BUTTON: 'submitButton'
};

export const PERIOD_HOURS_FIELD_MAP = {
  [ALLOCATION_PERIOD.ENTIRE_PERIOD]: 'hoursForEntirePeriod',
  [ALLOCATION_PERIOD.MONTH]: 'hoursPerMonth',
  [ALLOCATION_PERIOD.WEEK]: 'hoursPerWeek'
};

const fetchAndUpdateScheduledHours = async ({
  newValues,
  userUri,
  getScheduleHoursForUserAndDateRange,
  setTotalScheduledHours
}) => {
  if (!userUri) return newValues;

  const { scheduledHours } = await getScheduleHoursForUserAndDateRange(
    userUri,
    {
      startDate: mapRepliconDateToMidnightUTCString(newValues.startDate),
      endDate: mapRepliconDateToMidnightUTCString(newValues.endDate)
    }
  );

  setTotalScheduledHours(scheduledHours);

  return {
    ...newValues,
    hoursForEntirePeriod:
      scheduledHours > 0
        ? (newValues.percentageForEntirePeriod / 100) * scheduledHours
        : 0
  };
};

export const useSetResourceAllocationOnChangeHandlers = ({
  totalScheduledHours,
  setTotalScheduledHours,
  setIsLoading,
  setFocusField,
  userUri
}) => {
  const {
    values,
    setFieldValue,
    setValues,
    setFieldTouched
  } = useFormikContext();

  const {
    getScheduleHoursForUserAndDateRange
  } = useGetScheduledHoursForUserAndDateRangeCallback();

  const onStartDateChange = useCallback(
    async startDate => {
      const isStartDateAfterEndDate =
        compareDateObjects(startDate, values.endDate) === 1;

      const newEndDate = isStartDateAfterEndDate ? startDate : values.endDate;

      setFocusField(INPUT_FIELDS_MAP.STARTDATE_FIELD);
      setIsLoading(true);
      const newValues = await fetchAndUpdateScheduledHours({
        newValues: { ...values, startDate, endDate: newEndDate },
        userUri,
        getScheduleHoursForUserAndDateRange,
        setTotalScheduledHours
      });

      setValues(newValues);
      setIsLoading(false);
    },
    [
      getScheduleHoursForUserAndDateRange,
      setFocusField,
      setIsLoading,
      setTotalScheduledHours,
      setValues,
      userUri,
      values
    ]
  );

  const onEndDateChange = useCallback(
    async endDate => {
      const isEndDateBeforeStartDate =
        compareDateObjects(endDate, values.startDate) === -1;

      const newStartDate = isEndDateBeforeStartDate
        ? endDate
        : values.startDate;

      setFocusField(INPUT_FIELDS_MAP.ENDDATE_FIELD);
      setIsLoading(true);
      const newValues = await fetchAndUpdateScheduledHours({
        newValues: { ...values, startDate: newStartDate, endDate },
        userUri,
        getScheduleHoursForUserAndDateRange,
        setTotalScheduledHours
      });

      setValues(newValues);
      setIsLoading(false);
    },
    [
      getScheduleHoursForUserAndDateRange,
      setFocusField,
      setIsLoading,
      setTotalScheduledHours,
      setValues,
      userUri,
      values
    ]
  );

  const onClearAllocationChange = useCallback(
    ({ target: { checked } }) => {
      setFieldValue('isClearAllocationChecked', checked);
    },
    [setFieldValue]
  );

  const onSelectedAllocationPeriodChange = useCallback(
    (_, selectedTab) => {
      setFieldValue('selectedAllocationPeriod', selectedTab);
    },
    [setFieldValue]
  );

  const onHoursValueChange = useCallback(
    hours => {
      const { selectedAllocationPeriod } = values;
      const fieldName = PERIOD_HOURS_FIELD_MAP[selectedAllocationPeriod];

      setFieldTouched(fieldName, true, false);

      if (
        selectedAllocationPeriod === ALLOCATION_PERIOD.ENTIRE_PERIOD &&
        totalScheduledHours > 0
      ) {
        const percentage = (hours / totalScheduledHours) * 100;

        setValues({
          ...values,
          hoursForEntirePeriod: hours,
          percentageForEntirePeriod: percentage
        });
      } else {
        setFieldValue(fieldName, hours);
      }
    },
    [setFieldTouched, setFieldValue, setValues, totalScheduledHours, values]
  );

  const onPercentageValueChange = useCallback(
    percentage => {
      setFieldTouched('hoursForEntirePeriod', true, false);

      if (totalScheduledHours > 0) {
        const hours = roundToDecimals((percentage / 100) * totalScheduledHours);

        setValues({
          ...values,
          percentageForEntirePeriod: percentage,
          hoursForEntirePeriod: hours
        });
      } else {
        setFieldValue('percentageForEntirePeriod', percentage);
      }
    },
    [setFieldTouched, setFieldValue, setValues, totalScheduledHours, values]
  );

  return {
    onStartDateChange,
    onEndDateChange,
    onClearAllocationChange,
    onSelectedAllocationPeriodChange,
    onHoursValueChange,
    onPercentageValueChange
  };
};
